import { REPORT_ACCENT } from './theme.js';
/** Every connector must reference one unique node in an acyclic hierarchy. */
export function organisationGraphIsValid(nodes) {
    const byId = new Map(nodes.map((node) => [node.id, node]));
    if (byId.size !== nodes.length || nodes.length > 1000)
        return false;
    for (const node of nodes) {
        const visited = new Set();
        let current = node;
        while (current) {
            if (!current.id || visited.has(current.id))
                return false;
            visited.add(current.id);
            if (current.parentId === null)
                break;
            current = byId.get(current.parentId);
            if (!current)
                return false;
        }
    }
    return true;
}
/** Guard browser-retained drafts before rendering. This checks shapes and references, not server policy limits. */
export function isReportDocumentV2(value) {
    const record = (item) => !!item && typeof item === 'object' && !Array.isArray(item);
    const stringFields = (item, fields) => record(item) && fields.every((field) => typeof item[field] === 'string');
    const stringsOnly = (item) => Array.isArray(item) && item.every((value) => typeof value === 'string');
    const nullableNumber = (item) => item === null || (typeof item === 'number' && Number.isFinite(item));
    const scalar = (item) => typeof item === 'string' || nullableNumber(item);
    const marketShare = (item) => item === null ||
        (typeof item === 'number' && Number.isFinite(item) && item >= 0 && item <= 100);
    const objectArray = (item, valid) => Array.isArray(item) && item.every((value) => record(value) && valid(value));
    const optionalTitle = (item) => item.title === undefined || typeof item.title === 'string';
    const validBlock = (block) => {
        if (!stringFields(block, ['id', 'kind']) ||
            !block.id ||
            !optionalTitle(block) ||
            (block.enabled !== undefined && typeof block.enabled !== 'boolean') ||
            (block.optional !== undefined && typeof block.optional !== 'boolean'))
            return false;
        switch (block.kind) {
            case 'text':
                return (typeof block.markdown === 'string' &&
                    (block.authored === undefined || typeof block.authored === 'boolean'));
            case 'metrics':
                return objectArray(block.items, (item) => stringFields(item, ['label']) &&
                    scalar(item.value) &&
                    (item.detail === undefined || typeof item.detail === 'string'));
            case 'table':
                return (stringsOnly(block.columns) &&
                    Array.isArray(block.rows) &&
                    block.rows.every((row) => Array.isArray(row) && row.every(scalar)));
            case 'organisation':
                return (typeof block.caption === 'string' &&
                    objectArray(block.nodes, (node) => stringFields(node, ['id', 'label', 'detail']) &&
                        node.label.trim().length > 0 &&
                        (node.parentId === null || typeof node.parentId === 'string')) &&
                    organisationGraphIsValid(block.nodes));
            case 'bars':
                return ((block.caption === undefined || typeof block.caption === 'string') &&
                    objectArray(block.items, (item) => stringFields(item, ['label']) &&
                        nullableNumber(item.value) &&
                        (item.display === undefined || typeof item.display === 'string')));
            case 'swot':
                return objectArray(block.groups, (item) => typeof item.label === 'string' && stringsOnly(item.items));
            case 'forces':
                return ((block.caption === undefined || typeof block.caption === 'string') &&
                    objectArray(block.items, (item) => stringFields(item, ['label', 'value'])));
            case 'comparison':
                return (typeof block.caption === 'string' &&
                    objectArray(block.items, (item) => stringFields(item, ['label']) &&
                        item.label.trim().length > 0 &&
                        ['positive', 'negative'].every((key) => typeof item[key] === 'number' && Number.isSafeInteger(item[key]) && item[key] >= 0)));
            case 'radar':
                return (typeof block.caption === 'string' &&
                    stringsOnly(block.axes) &&
                    block.axes.length >= 3 &&
                    block.axes.every((axis) => axis.trim().length > 0) &&
                    new Set(block.axes).size === block.axes.length &&
                    typeof block.maximum === 'number' &&
                    Number.isFinite(block.maximum) &&
                    block.maximum > 0 &&
                    objectArray(block.series, (series) => typeof series.label === 'string' &&
                        series.label.trim().length > 0 &&
                        Array.isArray(series.values) &&
                        series.values.length === block.axes.length &&
                        series.values.every((value) => value === null ||
                            (typeof value === 'number' &&
                                Number.isFinite(value) &&
                                value >= 0 &&
                                value <= block.maximum))) &&
                    block.series.length > 0);
            case 'positioning':
                return (typeof block.caption === 'string' &&
                    objectArray(block.items, (item) => stringFields(item, ['label']) &&
                        item.label.trim().length > 0 &&
                        (item.priceQuality === null ||
                            ['low', 'medium', 'high'].includes(item.priceQuality)) &&
                        (item.geoScope === null ||
                            ['local', 'regional', 'national', 'international'].includes(item.geoScope)) &&
                        marketShare(item.marketSize)) &&
                    block.items.length <= 5_000);
            default:
                return false;
        }
    };
    if (!record(value) ||
        value.schemaVersion !== 2 ||
        typeof value.companyId !== 'string' ||
        !value.companyId)
        return false;
    if (!stringFields(value.metadata, ['title', 'subtitle', 'date', 'author']) ||
        !stringFields(value.theme, ['accent']))
        return false;
    if (!record(value.settings) ||
        ![
            'includeCover',
            'includeContents',
            'includePageNumbers',
            'includeFooter',
            'confidential',
        ].every((key) => typeof value.settings[key] === 'boolean'))
        return false;
    const source = value.sourceSnapshot;
    if (!record(source) ||
        !stringFields(source.company, ['id', 'name']) ||
        source.company.id !== value.companyId ||
        typeof source.capturedAt !== 'string' ||
        !Number.isFinite(new Date(source.capturedAt).valueOf()))
        return false;
    if (!stringFields(source.foundation, ['vision', 'mission']) ||
        !stringsOnly(source.foundation.values) ||
        !stringsOnly(source.loadErrors))
        return false;
    const objective = (item) => stringFields(item, ['id', 'category', 'goal', 'measure', 'byWhen', 'status', 'owner']);
    const executionItem = (item) => stringFields(item, ['id', 'title', 'status', 'startDate', 'targetDate', 'owner', 'parentId']) &&
        nullableNumber(item.progress);
    const findingGroup = (item) => stringFields(item, ['key', 'label']) &&
        objectArray(item.items, (finding) => stringFields(finding, ['id', 'content']) && nullableNumber(finding.impact));
    if (!objectArray(source.objectives, objective) ||
        !objectArray(source.pestel, (item) => stringFields(item, ['key', 'label']) &&
            stringsOnly(item.positive) &&
            stringsOnly(item.negative)))
        return false;
    if (!objectArray(source.fiveForces, (item) => stringFields(item, ['key', 'label', 'explanation', 'sourceKey']) &&
        (item.level === null || typeof item.level === 'string')))
        return false;
    if (!objectArray(source.swot, findingGroup) || !objectArray(source.successFactors, findingGroup))
        return false;
    if (!objectArray(source.competitors, (item) => stringFields(item, ['name', 'marketScope', 'strengths', 'weaknesses']) &&
        (item.id === undefined || typeof item.id === 'string') &&
        ['priceQuality', 'geoScope'].every((key) => item[key] === undefined || item[key] === null || typeof item[key] === 'string') &&
        (item.marketSize === undefined || marketShare(item.marketSize))) ||
        !objectArray(source.drivingForces, (item) => stringFields(item, ['name', 'commentary', 'impact'])))
        return false;
    const execution = source.execution;
    if (!record(execution) ||
        !objectArray(execution.objectives, objective) ||
        (execution.taskDetailsAvailable !== undefined &&
            typeof execution.taskDetailsAvailable !== 'boolean') ||
        !['strategies', 'projects', 'tasks'].every((key) => objectArray(execution[key], executionItem)))
        return false;
    if (!record(execution.counts) ||
        !record(execution.statusCounts) ||
        !['objectives', 'strategies', 'projects', 'tasks'].every((key) => typeof execution.counts[key] === 'number' &&
            Number.isFinite(execution.counts[key]) &&
            execution.counts[key] >= 0 &&
            record(execution.statusCounts[key]) &&
            Object.values(execution.statusCounts[key]).every((count) => typeof count === 'number' && Number.isFinite(count) && count >= 0)))
        return false;
    const layouts = ['narrative', 'visual', 'scorecard', 'matrix', 'roadmap'];
    if (!objectArray(value.sections, (section) => stringFields(section, ['id', 'title']) &&
        section.id &&
        typeof section.enabled === 'boolean' &&
        (section.appendix === undefined || typeof section.appendix === 'boolean') &&
        layouts.includes(section.layout) &&
        objectArray(section.blocks, validBlock)))
        return false;
    if (!objectArray(value.slides, (slide) => stringFields(slide, ['id', 'title', 'sectionId', 'takeaways', 'notes']) &&
        slide.id &&
        typeof slide.enabled === 'boolean' &&
        layouts.includes(slide.layout) &&
        stringsOnly(slide.blockIds)))
        return false;
    const sectionIds = value.sections.map((section) => section.id);
    const blockIds = value.sections.flatMap((section) => section.blocks.map((block) => block.id));
    const slideIds = value.slides.map((slide) => slide.id);
    if (new Set(sectionIds).size !== sectionIds.length ||
        new Set(blockIds).size !== blockIds.length ||
        new Set(slideIds).size !== slideIds.length)
        return false;
    return value.slides.every((slide) => slide.sectionId === 'cover'
        ? slide.blockIds.length === 0
        : value.sections.some((section) => section.id === slide.sectionId &&
            slide.blockIds.every((id) => section.blocks.some((block) => block.id === id))));
}
const object = (value) => value && typeof value === 'object' && !Array.isArray(value) ? value : {};
const array = (value) => (Array.isArray(value) ? value : []);
const text = (value) => typeof value === 'string' ? value.trim() : typeof value === 'number' ? String(value) : '';
const clean = (value) => {
    const result = text(value);
    return /^(?:complete your foundation section|analysis pending|not analy[sz]ed|not specified|to be defined|to be determined|tbd$)/i.test(result)
        ? ''
        : result;
};
const label = (value) => value
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]/g, ' ')
    .replace(/^./, (character) => character.toUpperCase());
const unique = (values) => [...new Set(values.filter(Boolean))];
const isoDate = (now) => {
    const date = now instanceof Date ? now : now ? new Date(now) : new Date();
    return Number.isFinite(date.valueOf()) ? date.toISOString() : new Date().toISOString();
};
const localDate = (value) => {
    const date = value instanceof Date ? value : new Date(value);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};
const dateOnly = (value) => {
    const date = clean(value);
    return /^\d{4}-\d{2}-\d{2}/.test(date) ? date.slice(0, 10) : date;
};
const hash = (value) => {
    let result = 2166136261;
    for (let i = 0; i < value.length; i++)
        result = Math.imul(result ^ value.charCodeAt(i), 16777619);
    return (result >>> 0).toString(36);
};
const stableId = (prefix, value, fallback) => `${prefix}-${text(object(value).id) || hash(fallback)}`;
const prose = (value) => Array.isArray(value)
    ? value.map(prose).filter(Boolean).join(', ')
    : clean(typeof value === 'object'
        ? object(value).content || object(value).name || object(value).value || object(value).text
        : value);
const strings = (value) => unique(array(value).map(prose));
const valuesList = (value) => {
    if (Array.isArray(value))
        return strings(value);
    if (typeof value !== 'string')
        return [];
    try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed))
            return strings(parsed);
    }
    catch {
        /* Older value fields may contain comma-separated text. */
    }
    return unique(value.split(',').map(clean));
};
const unwrap = (value) => {
    const record = object(value);
    return record.success !== undefined && record.data ? object(record.data) : record;
};
const findingImpact = (value) => {
    if (value === 'low')
        return 1;
    if (value === 'medium')
        return 2;
    if (value === 'high')
        return 3;
    return typeof value === 'number' && value >= 1 && value <= 3 ? value : null;
};
const SWOT_GROUPS = [
    ['strengths', 'Strengths'],
    ['weaknesses', 'Weaknesses'],
    ['opportunities', 'Opportunities'],
    ['threats', 'Threats'],
];
const SUCCESS_GROUPS = [
    ['productAttributes', 'Product attributes'],
    ['serviceCharacteristics', 'Service characteristics'],
    ['resources', 'Resources'],
    ['competitiveCapabilities', 'Competitive capabilities'],
    ['shortcomings', 'Shortcomings'],
];
const FORCE_KEYS = [
    { key: 'suppliers', label: 'Supplier power', aliases: ['suppliers', 'supplierPower'] },
    { key: 'buyers', label: 'Buyer power', aliases: ['buyers', 'buyerPower'] },
    {
        key: 'rivalry',
        label: 'Competitive rivalry',
        aliases: ['rivalFirms', 'rivalry', 'competitiveRivalry'],
    },
    {
        key: 'substitutes',
        label: 'Threat of substitutes',
        aliases: ['substitutes', 'threatOfSubstitutes'],
    },
    {
        key: 'newEntrants',
        label: 'Threat of new entrants',
        aliases: ['newEntrants', 'threatOfNewEntrants', 'barriersToEntry'],
    },
];
function normalizeFindings(value, groups) {
    const raw = object(value);
    return groups.map(([key, title]) => ({
        key,
        label: title,
        items: array(raw[key]).flatMap((item) => {
            const content = prose(item);
            return content
                ? [
                    {
                        id: stableId(key, item, content),
                        content,
                        impact: findingImpact(object(item).impact ?? object(item).impactLevel),
                    },
                ]
                : [];
        }),
    }));
}
function normalizeObjectives(value) {
    const grouped = Array.isArray(value)
        ? [['', value]]
        : Object.entries(object(value));
    const results = grouped.flatMap(([category, objectives]) => array(objectives).flatMap((item) => {
        const raw = object(item);
        const goal = clean(raw.goal || raw.objective || raw.title || raw.name);
        if (!goal)
            return [];
        const objectiveCategory = clean(raw.category || raw.type || category);
        const canonicalCategory = ['financial', 'customer', 'process', 'talent'].find((key) => objectiveCategory.toLowerCase().includes(key));
        return [
            {
                id: stableId('objective', raw, `${canonicalCategory || objectiveCategory}:${goal}`),
                category: objectiveCategory
                    ? label(canonicalCategory || objectiveCategory)
                    : 'Uncategorised',
                goal,
                measure: clean(raw.measureOptionLabel || raw.measure || raw.measureName),
                byWhen: dateOnly(raw.byWhen || raw.targetDate || raw.target),
                status: clean(raw.status),
                owner: clean(raw.assigneeName || raw.owner || raw.assignedToName),
            },
        ];
    }));
    return results.filter((item, index) => results.findIndex((other) => other.id === item.id) === index);
}
function normalizeExecution(rawExecution) {
    const objectiveInput = array(rawExecution.objectives);
    const strategies = [
        ...array(rawExecution.strategies),
        ...objectiveInput.flatMap((item) => array(item.strategies).map((strategy) => ({
            ...strategy,
            objectiveId: strategy.objectiveId || item.id,
        }))),
    ];
    const projects = [
        ...array(rawExecution.projects),
        ...strategies.flatMap((item) => array(item.projects).map((project) => ({
            ...project,
            strategyId: project.strategyId || item.id,
        }))),
    ];
    const tasks = [
        ...array(Array.isArray(rawExecution.tasks) ? rawExecution.tasks : object(rawExecution.tasks).all),
        ...projects.flatMap((item) => array(item.tasks).map((task) => ({ ...task, projectId: task.projectId || item.id }))),
    ];
    const normalizeItems = (items, kind) => {
        const result = items.flatMap((item) => {
            const raw = object(item);
            const title = clean(raw.title || raw.name);
            if (!title)
                return [];
            const progressValue = raw.percentComplete ?? raw.progress;
            return [
                {
                    id: stableId(kind, raw, `${raw.objectiveId || raw.strategyId || raw.projectId || ''}:${title}`),
                    title,
                    status: clean(raw.status),
                    startDate: dateOnly(raw.startDate),
                    targetDate: dateOnly(raw.dueDate || raw.targetDate),
                    owner: clean(raw.assigneeName || raw.projectManagerName || raw.owner),
                    parentId: clean(raw.objectiveId || raw.strategyId || raw.projectId),
                    progress: typeof progressValue === 'number' &&
                        Number.isFinite(progressValue) &&
                        progressValue >= 0 &&
                        progressValue <= 100
                        ? progressValue
                        : null,
                },
            ];
        });
        return result.filter((item, index) => result.findIndex((other) => other.id === item.id) === index);
    };
    const result = {
        objectives: normalizeObjectives(objectiveInput),
        strategies: normalizeItems(strategies, 'strategy'),
        projects: normalizeItems(projects, 'project'),
        tasks: normalizeItems(tasks, 'task'),
    };
    const summary = object(rawExecution.progressSummary);
    const count = (key) => {
        const reported = summary[`total${label(key)}`];
        return typeof reported === 'number' && Number.isFinite(reported)
            ? Math.max(reported, result[key].length)
            : result[key].length;
    };
    const counts = {
        objectives: count('objectives'),
        strategies: count('strategies'),
        projects: count('projects'),
        tasks: count('tasks'),
    };
    const statusCounts = {};
    for (const key of ['objectives', 'strategies', 'projects', 'tasks']) {
        statusCounts[key] = result[key].reduce((statuses, item) => {
            const status = item.status || 'not_recorded';
            statuses[status] = (statuses[status] || 0) + 1;
            return statuses;
        }, {});
        // The hierarchy endpoint supplies task totals without task rows. Keep its actual status counts.
        if (result[key].length < counts[key]) {
            const completed = summary[`${key}Completed`];
            const active = summary[`${key}InProgress`];
            if (typeof completed === 'number' && completed >= 0)
                statusCounts[key].completed = completed;
            if (typeof active === 'number' && active >= 0)
                statusCounts[key].in_progress = active;
        }
    }
    return {
        ...result,
        counts,
        statusCounts,
        taskDetailsAvailable: Array.isArray(rawExecution.tasks) || Array.isArray(object(rawExecution.tasks).all),
    };
}
/** Accepts the raw APIs and the former VisualReportPage's transformed data. Missing ratings stay missing. */
export function normalizeReportSource(company, data, now) {
    const raw = unwrap(data);
    const summary = unwrap(raw.summary || raw.summaryData || raw);
    const internal = object(summary.internal);
    const external = object(summary.external);
    const foundationResponse = unwrap(raw.foundation || raw.foundationData);
    const foundation = object(foundationResponse.foundationData || foundationResponse);
    const firstStatement = (value, prefix, fallback) => {
        const record = object(value);
        return (clean(typeof value === 'string'
            ? value
            : record.statement || record[`${prefix}1`] || record[`${prefix}2`] || record[`${prefix}3`]) || clean(object(fallback).statement || fallback));
    };
    const valuesRaw = foundation.values ?? internal.values ?? raw.valuesData;
    const values = Array.isArray(valuesRaw) || typeof valuesRaw === 'string'
        ? valuesList(valuesRaw)
        : unique(['values1', 'values2', 'values3'].flatMap((key) => valuesList(object(valuesRaw)[key])));
    const execution = normalizeExecution(unwrap(raw.execution || raw.strategicExecution || raw.executionData));
    const objectives = normalizeObjectives(foundation.objectives || raw.objectivesData || internal.objectives);
    // The execution endpoint includes the current objective measure and ISO target date.
    const objectivesById = new Map(execution.objectives.map((item) => [item.id, item]));
    const mergedObjectives = objectives.map((item) => {
        const current = objectivesById.get(item.id) ||
            execution.objectives.find((candidate) => candidate.goal === item.goal &&
                candidate.category.toLowerCase() === item.category.toLowerCase());
        return current
            ? {
                ...item,
                ...current,
                measure: current.measure || item.measure,
                byWhen: current.byWhen || item.byWhen,
            }
            : item;
    });
    for (const item of execution.objectives)
        if (!mergedObjectives.some((existing) => existing.id === item.id ||
            (existing.goal === item.goal && existing.category === item.category)))
            mergedObjectives.push(item);
    const pestelRaw = raw.pestelData || external.pestel || {};
    const pestelEntries = Array.isArray(pestelRaw)
        ? pestelRaw.map((item) => [clean(item.factor || item.key), item])
        : Object.entries(object(pestelRaw));
    const pestel = pestelEntries.map(([key, item]) => ({
        key: key.toLowerCase(),
        label: label(key),
        positive: strings(object(item).positive),
        negative: strings(object(item).negative),
    }));
    const forces = object(raw.fiveForcesRawData || external.fiveForces);
    const forceList = array(raw.fiveForcesListData);
    const fiveForces = FORCE_KEYS.map((force) => {
        const sourceKey = force.aliases.find((key) => forces[key] !== undefined) || force.key;
        const listItem = forceList.find((item) => force.aliases.some((key) => label(key).toLowerCase() === text(item.force).toLowerCase()));
        const value = object(forces[sourceKey] || listItem);
        const hasLoadFailure = /loading error/i.test(text(value.explanation));
        return {
            key: force.key,
            label: sourceKey === 'barriersToEntry' ? 'Barriers to entry' : force.label,
            level: hasLoadFailure ? null : clean(value.level) || null,
            explanation: hasLoadFailure ? '' : clean(value.explanation),
            sourceKey,
        };
    });
    const competitors = array(raw.competitorsData || external.competitors).flatMap((item) => {
        const name = clean(item.name);
        return name
            ? [
                {
                    ...(clean(item.id) ? { id: clean(item.id) } : {}),
                    name,
                    marketScope: clean(item.geoScope ?? item.marketScope),
                    strengths: prose(item.strengths),
                    weaknesses: prose(item.weaknesses),
                    priceQuality: clean(item.priceQuality) || null,
                    geoScope: clean(item.geoScope) || null,
                    marketSize: typeof item.marketSize === 'number' &&
                        Number.isFinite(item.marketSize) &&
                        item.marketSize >= 0 &&
                        item.marketSize <= 100
                        ? item.marketSize
                        : null,
                },
            ]
            : [];
    });
    const drivingForces = array(raw.drivingForcesData || external.drivingForces).flatMap((item) => {
        const name = clean(item.name || item.title || item.content);
        return name
            ? [
                {
                    name,
                    commentary: clean(item.commentary || item.description),
                    impact: clean(item.impactLevel || item.impact),
                },
            ]
            : [];
    });
    const loadErrors = Array.isArray(raw.loadErrors)
        ? raw.loadErrors
            .map((item) => typeof item === 'string'
            ? item
            : [clean(item.source), clean(item.message || item.error)].filter(Boolean).join(': '))
            .filter(Boolean)
        : Object.entries(object(raw.loadErrors)).map(([key, error]) => `${label(key)}: ${text(error) || 'Could not load source data'}`);
    return {
        capturedAt: isoDate(now),
        company: {
            id: company.id,
            name: company.name,
            ...Object.fromEntries(['logo', 'industry', 'size']
                .filter((key) => typeof company[key] === 'string')
                .map((key) => [key, company[key]])),
        },
        foundation: {
            vision: firstStatement(foundation.vision, 'vision', raw.visionData || internal.vision),
            mission: firstStatement(foundation.mission, 'mission', raw.missionData || internal.mission),
            values,
        },
        objectives: mergedObjectives,
        pestel,
        fiveForces,
        swot: normalizeFindings(raw.swotWithImpact || internal.swot, SWOT_GROUPS),
        successFactors: normalizeFindings(raw.successFactorsWithImpact || internal.successFactors, SUCCESS_GROUPS),
        competitors,
        drivingForces,
        execution,
        loadErrors: unique(loadErrors),
    };
}
const missing = (value) => value || 'Not recorded';
const bulletList = (items) => items.map((item) => `- ${item}`).join('\n');
const commentary = (sectionId) => ({
    id: `${sectionId}-commentary`,
    kind: 'text',
    title: 'Commentary',
    markdown: '',
    authored: true,
});
const sourceText = (id, markdown, title) => ({
    id,
    kind: 'text',
    title,
    markdown,
    authored: false,
});
const section = (id, title, layout, blocks, appendix = false) => ({
    id,
    title,
    layout,
    enabled: true,
    ...(appendix ? { appendix: true } : {}),
    blocks: [...blocks, commentary(id)],
});
/** Explicit source excerpts keep the starting deck concise without changing the full report facts. */
const excerpt = (value, maximum = 140) => {
    const compact = value.replace(/\s+/g, ' ').trim();
    if (compact.length <= maximum)
        return compact;
    const prefix = compact.slice(0, maximum - 1);
    const space = prefix.lastIndexOf(' ');
    return `${(space > 0 ? prefix.slice(0, space) : prefix).trimEnd()}…`;
};
const slideHighlights = (items, maximum = 3) => bulletList(items
    .filter(Boolean)
    .slice(0, maximum)
    .map((item) => excerpt(item)));
function createChartOptions(source) {
    const option = { optional: true, enabled: false };
    const recordedCategory = (value, choices) => choices.find((category) => category === clean(value).toLowerCase()) ?? null;
    const forceScale = {
        'very strong': 1,
        'very high': 1,
        strong: 2,
        high: 2,
        moderate: 3,
        medium: 3,
        weak: 4,
        low: 4,
        'very weak': 5,
        'very low': 5,
    };
    const forceOrder = ['rivalry', 'suppliers', 'buyers', 'substitutes', 'newEntrants'];
    const forceAxes = forceOrder.map((key) => {
        const recorded = source.fiveForces.find((force) => force.key === key);
        return {
            label: recorded?.label || FORCE_KEYS.find((force) => force.key === key).label,
            value: recorded?.level ? (forceScale[recorded.level.toLowerCase().trim()] ?? null) : null,
        };
    });
    const missingForceAxes = forceAxes
        .filter((axis) => axis.value === null)
        .map((axis) => axis.label);
    const impactRadar = (id, title, sourceGroups, groups) => {
        const axes = groups.map(([key, label]) => {
            const group = sourceGroups.find((item) => item.key === key);
            const items = group?.items || [];
            return {
                label: group?.label || label,
                value: !items.length || items.some((item) => item.impact === null)
                    ? null
                    : items.reduce((total, item) => total + item.impact, 0),
            };
        });
        const missingAxes = axes.filter((axis) => axis.value === null).map((axis) => axis.label);
        return {
            id,
            kind: 'radar',
            title,
            ...option,
            axes: axes.map((axis) => axis.label),
            series: [{ label: 'Total recorded impact', values: axes.map((axis) => axis.value) }],
            maximum: Math.max(10, ...axes.flatMap((axis) => (axis.value === null ? [] : [axis.value]))),
            caption: 'Sum of selected finding impact ratings: Low = 1, Medium = 2, High = 3. ' +
                'These scores measure recorded impact, not company performance.' +
                (missingAxes.length
                    ? ` Not plotted because findings or impact ratings are missing: ${missingAxes.join(', ')}.`
                    : ''),
        };
    };
    const swotOrder = ['strengths', 'opportunities', 'threats', 'weaknesses'].map((key) => SWOT_GROUPS.find((group) => group[0] === key));
    return new Map([
        [
            'external-environment',
            [
                {
                    id: 'external-pestel-comparison',
                    kind: 'comparison',
                    title: 'PESTEL impact chart',
                    ...option,
                    items: source.pestel
                        .filter((factor) => factor.label.trim())
                        .map((factor) => ({
                        label: factor.label,
                        positive: factor.positive.length,
                        negative: factor.negative.length,
                    })),
                    caption: 'Counts of recorded positive and negative findings. Counts do not measure impact severity or business performance.',
                },
                {
                    id: 'external-forces-radar',
                    kind: 'radar',
                    title: 'Five Forces radar',
                    ...option,
                    axes: forceAxes.map((axis) => axis.label),
                    series: [{ label: 'Recorded level', values: forceAxes.map((axis) => axis.value) }],
                    maximum: 5,
                    caption: 'Recorded level scale: Very Strong = 1, Strong = 2, Moderate = 3, Weak = 4, Very Weak = 5. ' +
                        'High/Low use Strong/Weak positions. Axes retain their source measures, including barriers to entry; this is not a company performance score.' +
                        (missingForceAxes.length
                            ? ` Ratings not recorded: ${missingForceAxes.join(', ')}.`
                            : ''),
                },
            ],
        ],
        [
            'swot-capabilities',
            [
                impactRadar('swot-impact-radar', 'SWOT impact radar', source.swot, swotOrder),
                impactRadar('capabilities-success-radar', 'Success factors radar', source.successFactors, SUCCESS_GROUPS),
            ],
        ],
        [
            'success-factors',
            [
                impactRadar('success-factors-radar', 'Success factors radar', source.successFactors, SUCCESS_GROUPS),
            ],
        ],
        [
            'competitors',
            [
                {
                    id: 'competitors-positioning',
                    kind: 'positioning',
                    title: 'Competitive positioning matrix',
                    ...option,
                    items: source.competitors.map((competitor) => ({
                        label: competitor.name,
                        priceQuality: recordedCategory(competitor.priceQuality, [
                            'low',
                            'medium',
                            'high',
                        ]),
                        geoScope: recordedCategory(competitor.geoScope || competitor.marketScope, [
                            'local',
                            'regional',
                            'national',
                            'international',
                        ]),
                        marketSize: competitor.marketSize ?? null,
                    })),
                    caption: 'Positions use recorded geographic scope and price/quality categories, not a performance or threat score. ' +
                        'Market share is the recorded percentage (0–100%); it is not inferred from position. ' +
                        'Missing or unrecognized categories are not placed in the matrix. Missing market share remains not recorded.' +
                        (source.loadErrors.some((error) => /assessment summary|competitor/i.test(error))
                            ? ' Competitor source data could not be refreshed.'
                            : ''),
                },
            ],
        ],
    ]);
}
function createSourceSections(source) {
    const { foundation, objectives, execution } = source;
    const chartOptions = createChartOptions(source);
    const unavailable = (key) => source.loadErrors.some((error) => (/execution/i.test(error) && !(key === 'tasks' && execution.taskDetailsAvailable)) ||
        new RegExp(`\\b${key}\\b`, 'i').test(error));
    const metrics = {
        id: 'execution-counts',
        kind: 'metrics',
        items: ['objectives', 'strategies', 'projects', 'tasks'].map((key) => ({
            label: label(key),
            value: unavailable(key) ? 'Unavailable' : String(execution.counts[key]),
            detail: unavailable(key)
                ? 'Source could not be loaded'
                : execution[key].length < execution.counts[key] &&
                    execution.statusCounts[key].completed === undefined
                    ? 'Completion status unavailable'
                    : `${execution.statusCounts[key].completed || 0} marked completed`,
        })),
    };
    const pestelBars = {
        id: 'external-pestel-bars',
        kind: 'bars',
        title: 'Recorded PESTEL findings',
        items: source.pestel.map((item) => ({
            label: item.label,
            value: item.positive.length + item.negative.length,
            display: `${item.positive.length} positive / ${item.negative.length} negative`,
        })),
        caption: 'Number of recorded positive and negative findings. Counts do not measure severity or business performance.',
    };
    const forces = {
        id: 'external-forces',
        kind: 'forces',
        items: source.fiveForces.map((item) => ({
            label: item.label,
            value: missing(item.level || ''),
        })),
        caption: 'Recorded assessment levels. Barriers to entry retains the source measure; it is not inverted into entrant threat.',
    };
    const swot = {
        id: 'swot-matrix',
        kind: 'swot',
        groups: source.swot.map((group) => ({
            label: group.label,
            items: group.items.map((item) => item.content),
        })),
    };
    const executionRows = [
        ...execution.objectives.map((item) => [
            'Objective',
            item.goal,
            missing(item.status),
            missing(item.byWhen),
            missing(item.owner),
        ]),
        ...execution.strategies.map((item) => [
            'Strategy',
            item.title,
            missing(item.status),
            missing(item.targetDate),
            missing(item.owner),
        ]),
        ...execution.projects.map((item) => [
            'Project',
            item.title,
            missing(item.status),
            missing(item.targetDate),
            missing(item.owner),
        ]),
        ...execution.tasks.map((item) => [
            'Task',
            item.title,
            missing(item.status),
            missing(item.targetDate),
            missing(item.owner),
        ]),
    ];
    const brief = [
        foundation.vision ? `**Vision**\n\n${foundation.vision}` : '',
        foundation.mission ? `**Mission**\n\n${foundation.mission}` : '',
        objectives.length ? `**Objectives**\n\n${bulletList(objectives.map((item) => item.goal))}` : '',
    ]
        .filter(Boolean)
        .join('\n\n');
    const foundationMarkdown = `## Vision\n\n${missing(foundation.vision)}\n\n## Mission\n\n${missing(foundation.mission)}\n\n## Values\n\n${foundation.values.length ? bulletList(foundation.values) : 'Not recorded'}`;
    const successItems = source.successFactors.flatMap((group) => group.items.map((item) => ({
        label: item.content,
        value: item.impact,
        display: item.impact === null
            ? 'Impact not recorded'
            : `${['', 'Low', 'Medium', 'High'][item.impact]} impact`,
    })));
    const sections = [
        section('executive-brief', 'Executive brief', 'narrative', [
            sourceText('executive-brief-summary', brief ||
                (source.loadErrors.length
                    ? 'Strategic source data is incomplete. Refresh source data to retry.'
                    : 'No strategic foundation or objectives have been recorded.')),
            {
                id: 'executive-brief-metrics',
                kind: 'metrics',
                items: [
                    {
                        label: 'Strategic objectives',
                        value: unavailable('objectives') && !objectives.length
                            ? 'Unavailable'
                            : String(objectives.length),
                    },
                    {
                        label: 'Selected strategies',
                        value: unavailable('strategies') ? 'Unavailable' : String(execution.counts.strategies),
                    },
                    {
                        label: 'Projects',
                        value: unavailable('projects') ? 'Unavailable' : String(execution.counts.projects),
                    },
                ],
            },
        ]),
        section('foundation', 'Strategic foundation', 'narrative', [
            sourceText('foundation-statements', foundationMarkdown),
        ]),
        section('objectives', 'Strategic objectives', 'scorecard', [
            {
                id: 'objectives-table',
                kind: 'table',
                columns: ['Perspective', 'Objective', 'Measure', 'Target date', 'Status'],
                rows: objectives.map((item) => [
                    item.category,
                    item.goal,
                    missing(item.measure),
                    missing(item.byWhen),
                    missing(item.status),
                ]),
            },
            ...(!objectives.length
                ? [sourceText('objectives-empty', 'No strategic objectives have been recorded.')]
                : []),
        ]),
        section('external-environment', 'External environment', 'visual', [
            pestelBars,
            forces,
            ...chartOptions.get('external-environment'),
        ]),
        section('swot-capabilities', 'SWOT and capabilities', 'matrix', [
            swot,
            ...chartOptions.get('swot-capabilities'),
        ]),
        section('execution-progress', 'Execution and progress', 'roadmap', [
            metrics,
            {
                id: 'execution-roadmap',
                kind: 'table',
                title: 'Recorded work and milestones',
                columns: ['Type', 'Work item', 'Status', 'Target date', 'Owner'],
                rows: executionRows,
            },
            ...(!executionRows.length
                ? [
                    sourceText('execution-empty', source.loadErrors.some((error) => /execution|projects|strategies|tasks/i.test(error))
                        ? 'Execution source data is incomplete. Refresh source data to retry.'
                        : 'No execution work has been recorded.'),
                ]
                : []),
            ...(execution.counts.tasks > execution.tasks.length
                ? [
                    sourceText('execution-task-detail', 'Task details are not returned by this source. Recorded task counts are shown above.'),
                ]
                : []),
        ]),
        section('decisions', 'Decisions and next actions', 'narrative', [
            { id: 'decisions-summary', kind: 'text', markdown: '', authored: true },
        ]),
        section('pestel', 'Appendix: PESTEL findings', 'visual', [
            {
                id: 'pestel-findings',
                kind: 'table',
                columns: ['Factor', 'Direction', 'Finding'],
                rows: source.pestel.flatMap((item) => [
                    ...item.positive.map((finding) => [item.label, 'Positive', finding]),
                    ...item.negative.map((finding) => [item.label, 'Negative', finding]),
                ]),
            },
        ], true),
        section('five-forces', 'Appendix: Five Forces', 'visual', [
            {
                id: 'five-forces-findings',
                kind: 'table',
                columns: ['Force', 'Recorded level', 'Source commentary'],
                rows: source.fiveForces.map((item) => [
                    item.label,
                    missing(item.level || ''),
                    missing(item.explanation),
                ]),
            },
        ], true),
        section('success-factors', 'Appendix: Success factors', 'visual', [
            {
                id: 'success-factors-bars',
                kind: 'bars',
                title: 'Selected success factors',
                items: successItems,
                caption: 'Recorded impact rating: Low = 1, Medium = 2, High = 3. Missing ratings are not assigned a score.',
            },
            ...chartOptions.get('success-factors'),
        ], true),
        section('competitors', 'Appendix: Competitive landscape', 'scorecard', [
            {
                id: 'competitors-table',
                kind: 'table',
                columns: [
                    'Competitor',
                    'Market scope',
                    'Price/quality',
                    'Recorded market share',
                    'Strengths',
                    'Weaknesses',
                ],
                rows: source.competitors.map((item) => [
                    item.name,
                    missing(item.marketScope),
                    missing(item.priceQuality || ''),
                    item.marketSize === null || item.marketSize === undefined
                        ? 'Not recorded'
                        : `${item.marketSize}%`,
                    missing(item.strengths),
                    missing(item.weaknesses),
                ]),
            },
            ...chartOptions.get('competitors'),
        ], true),
        section('driving-forces', 'Appendix: Industry driving forces', 'narrative', [
            {
                id: 'driving-forces-table',
                kind: 'table',
                columns: ['Driving force', 'Commentary', 'Recorded impact'],
                rows: source.drivingForces.map((item) => [
                    item.name,
                    missing(item.commentary),
                    missing(item.impact),
                ]),
            },
        ], true),
    ];
    if (source.loadErrors.length)
        sections[0].blocks.push(sourceText('source-load-errors', `## Source data unavailable\n\n${bulletList(source.loadErrors)}\n\nRefresh source data to retry. These sections may be incomplete.`));
    return sections;
}
/** Creates one company-branded report and eight curated starting slides. */
export function createReportDocument(company, source, author = '', now) {
    const sections = createSourceSections(source);
    const slide = (id, title, sectionId, blockIds, takeaways, layout) => ({
        id,
        title,
        enabled: true,
        sectionId,
        blockIds,
        takeaways,
        notes: '',
        layout,
    });
    const priorityItems = [
        ...source.execution.strategies.map((item) => item.title),
        ...source.execution.projects.map((item) => item.title),
    ];
    return {
        schemaVersion: 2,
        companyId: company.id,
        metadata: {
            title: `${company.name} strategic report`,
            subtitle: 'Direction, priorities and delivery',
            date: localDate(isoDate(now || source.capturedAt)),
            author,
        },
        theme: { accent: REPORT_ACCENT },
        settings: {
            includeCover: true,
            includeContents: true,
            includePageNumbers: true,
            includeFooter: true,
            confidential: false,
        },
        sourceSnapshot: source,
        sections,
        slides: [
            slide('slide-cover', company.name, 'cover', [], '', 'narrative'),
            slide('slide-direction', 'Our direction', 'foundation', [], slideHighlights([
                source.foundation.vision,
                source.foundation.mission,
                ...source.foundation.values,
            ]), 'narrative'),
            slide('slide-objectives', 'Strategic objectives', 'objectives', [], slideHighlights(source.objectives.map((item) => item.goal)), 'scorecard'),
            slide('slide-environment', 'External environment', 'external-environment', ['external-pestel-bars'], '', 'visual'),
            slide('slide-swot', 'SWOT and capabilities', 'swot-capabilities', [], slideHighlights(source.swot.flatMap((group) => group.items.slice(0, 1).map((item) => `${group.label}: ${item.content}`)), 4), 'matrix'),
            slide('slide-priorities', 'Delivery priorities', 'execution-progress', [], slideHighlights(priorityItems), 'roadmap'),
            slide('slide-progress', 'Progress and milestones', 'execution-progress', ['execution-counts'], slideHighlights(source.execution.projects
                .filter((item) => item.targetDate)
                .map((item) => `${item.title} — ${item.targetDate}`)), 'scorecard'),
            slide('slide-decisions', 'Decisions and next actions', 'decisions', ['decisions-summary'], '', 'narrative'),
        ],
    };
}
const LEGACY_IDS = {
    'executive-summary': 'executive-brief',
    'executive-summary-page': 'executive-brief',
    'foundation-page': 'foundation',
    'vision-mission': 'foundation',
    'objectives-page': 'objectives',
    'external-assessment-page': 'external-environment',
    'external-assessment': 'external-environment',
    'internal-assessment-page': 'swot-capabilities',
    'internal-assessment': 'swot-capabilities',
    swot: 'swot-capabilities',
    'swot-page': 'swot-capabilities',
    'strategic-execution': 'execution-progress',
    'strategy-execution-page': 'execution-progress',
    'progress-overview-page': 'execution-progress',
    recommendations: 'decisions',
    'pestel-page': 'pestel',
    'five-forces-page': 'five-forces',
    'industry-success-factors-page': 'success-factors',
    'competitor-analysis-page': 'competitors',
    'driving-forces-page': 'driving-forces',
};
const LAYOUTS = ['narrative', 'visual', 'scorecard', 'matrix', 'roadmap'];
const sourceSectionId = (id) => {
    const baseId = id.replace(/(?:-legacy-\d+)+$/, '');
    return LEGACY_IDS[baseId] || baseId;
};
const SUPPORTED_SECTION_IDS = new Set([
    'cover',
    'contents',
    'executive-brief',
    'foundation',
    'objectives',
    'external-environment',
    'external',
    'pestel',
    'five-forces',
    'competitors',
    'driving-forces',
    'swot-capabilities',
    'capabilities',
    'success-factors',
    'execution-progress',
    'execution',
    'decisions',
    'priorities',
    'progress',
    'key-metrics',
]);
/** Unknown legacy sections remain editable, but only supported section kinds can request AI drafts. */
export function isReportSectionSupported(sectionId) {
    return SUPPORTED_SECTION_IDS.has(sourceSectionId(sectionId));
}
const upgradedAccent = (accent) => ['#25635b', '#315c57'].includes(accent.toLowerCase()) ? REPORT_ACCENT : accent;
/** Add selectable charts from the saved snapshot without replacing authored content or deck curation. */
export function upgradeReportDocument(document) {
    const options = createChartOptions(document.sourceSnapshot);
    const existingIds = new Set(document.sections.flatMap((section) => section.blocks.map((block) => block.id)));
    let changed = false;
    const sections = document.sections.map((section) => {
        const canonicalId = sourceSectionId(section.id);
        const missingOptions = (options.get(canonicalId) || []).flatMap((block) => {
            const id = section.id === canonicalId ? block.id : `${section.id}-${block.id}`;
            if (existingIds.has(id))
                return [];
            existingIds.add(id);
            return [{ ...block, id }];
        });
        if (!missingOptions.length)
            return section;
        changed = true;
        return { ...section, blocks: [...section.blocks, ...missingOptions] };
    });
    const accent = upgradedAccent(document.theme.accent);
    if (!changed && accent === document.theme.accent)
        return document;
    return { ...document, theme: { ...document.theme, accent }, sections };
}
/** Legacy configuration fields remain on the configuration record; this only adapts its editable document. */
export function adaptReportConfiguration(config, company, source, author = '') {
    const record = unwrap(config);
    const saved = object(record.document);
    if (saved.schemaVersion === 2 &&
        saved.companyId === company.id &&
        Array.isArray(saved.sections) &&
        Array.isArray(saved.slides))
        return upgradeReportDocument(JSON.parse(JSON.stringify(saved)));
    const document = createReportDocument(company, source, author);
    const settings = object(record.settings);
    for (const key of Object.keys(document.settings))
        if (typeof settings[key] === 'boolean')
            document.settings[key] = settings[key];
    const accent = clean(object(object(record.style).colors).primary ||
        object(object(record.style).colors).accent ||
        object(record.theme).accent);
    if (/^#[\da-f]{6}$/i.test(accent))
        document.theme.accent = upgradedAccent(accent);
    if (clean(record.title))
        document.metadata.title = clean(record.title);
    if (clean(record.subtitle))
        document.metadata.subtitle = clean(record.subtitle);
    const legacySections = array(record.sections).length
        ? array(record.sections)
        : array(record.pages);
    if (!legacySections.length)
        return document;
    const used = new Set();
    const adapted = [];
    for (const [index, value] of legacySections
        .map((value, index) => ({ value, index }))
        .sort((a, b) => (typeof a.value.order === 'number' ? a.value.order : a.index) -
        (typeof b.value.order === 'number' ? b.value.order : b.index))
        .map(({ value, index }) => [index, value])) {
        const legacy = object(value);
        const originalId = clean(legacy.id) || `legacy-${index}`;
        if (originalId === 'cover' || originalId === 'cover-page') {
            document.settings.includeCover = legacy.enabled !== false;
            if (clean(legacy.customTitle || legacy.title))
                document.metadata.title = clean(legacy.customTitle || legacy.title);
            continue;
        }
        if (originalId === 'toc-page' || originalId === 'contents') {
            document.settings.includeContents = legacy.enabled !== false;
            continue;
        }
        const mappedId = LEGACY_IDS[originalId] || originalId;
        const defaultSection = document.sections.find((item) => item.id === mappedId);
        const preferredId = used.has(mappedId)
            ? originalId === mappedId
                ? `${mappedId}-legacy-${index}`
                : originalId
            : mappedId;
        let id = preferredId;
        let suffix = index;
        while (used.has(id))
            id = `${preferredId}-legacy-${suffix++}`;
        used.add(id);
        const defaultBlocks = defaultSection
            ? defaultSection.blocks
                .filter((block) => !(block.kind === 'text' && block.authored))
                .map((block) => ({ ...block, id: id === mappedId ? block.id : `${id}-${block.id}` }))
            : [];
        const content = unique([
            clean(legacy.customDescription),
            clean(legacy.customContent),
            clean(legacy.content),
            ...array(legacy.sections).map((item) => clean(item.content)),
        ]).join('\n\n');
        const blocks = [
            ...defaultBlocks,
            ...(content
                ? [{ id: `${id}-legacy-content`, kind: 'text', markdown: content, authored: true }]
                : [commentary(id)]),
        ];
        adapted.push({
            id,
            title: clean(legacy.customTitle || legacy.title || legacy.sectionName) ||
                defaultSection?.title ||
                label(originalId),
            enabled: legacy.enabled !== false,
            layout: LAYOUTS.includes(legacy.layout)
                ? legacy.layout
                : defaultSection?.layout || 'narrative',
            ...(defaultSection?.appendix ? { appendix: true } : {}),
            blocks,
        });
    }
    // A legacy selection is preserved exactly; new default sections are available but initially hidden.
    document.sections = [
        ...adapted,
        ...document.sections
            .filter((item) => !used.has(item.id))
            .map((item) => ({ ...item, enabled: false })),
    ];
    return document;
}
/** Refresh source blocks only. Section decisions, authored blocks and all deck curation remain unchanged. */
export function refreshReportSource(document, source) {
    if (source.company.id !== document.companyId)
        throw new Error('Source data belongs to a different company');
    const replacements = new Map(createSourceSections(source).map((item) => [item.id, item]));
    const sections = document.sections.map((current) => {
        const canonicalId = sourceSectionId(current.id);
        const replacement = replacements.get(canonicalId);
        if (!replacement)
            return current;
        const authoredIds = new Set(current.blocks
            .filter((block) => block.kind === 'text' && block.authored)
            .map((block) => block.id));
        const replacementBlocks = new Map(replacement.blocks
            .filter((block) => !(block.kind === 'text' && block.authored))
            .map((block) => {
            const refreshed = {
                ...block,
                id: current.id === canonicalId ? block.id : `${current.id}-${block.id}`,
            };
            return [refreshed.id, refreshed];
        }));
        const updated = current.blocks.flatMap((block) => {
            if (block.kind === 'text' && block.authored)
                return [block];
            const fresh = replacementBlocks.get(block.id);
            replacementBlocks.delete(block.id);
            return fresh
                ? [
                    {
                        ...fresh,
                        ...(block.enabled !== undefined ? { enabled: block.enabled } : {}),
                        ...(block.optional !== undefined ? { optional: block.optional } : {}),
                    },
                ]
                : [];
        });
        return {
            ...current,
            blocks: [
                ...updated,
                ...[...replacementBlocks.values()].filter((block) => !authoredIds.has(block.id)),
            ],
        };
    });
    const selectedBlocks = new Map(sections.map((section) => [section.id, new Set(section.blocks.map((block) => block.id))]));
    return {
        ...document,
        sourceSnapshot: source,
        sections,
        slides: document.slides.map((slide) => ({
            ...slide,
            blockIds: slide.blockIds.filter((id) => selectedBlocks.get(slide.sectionId)?.has(id)),
        })),
    };
}
/** Supplies actual normalized facts to the AI endpoint, with no inferred scores or discarded sections. */
export function getSectionAnalysis(document, sectionId) {
    const source = document.sourceSnapshot;
    const sectionIdMapped = sourceSectionId(sectionId);
    let analysisData;
    switch (sectionIdMapped) {
        case 'foundation':
            analysisData = { foundation: source.foundation };
            break;
        case 'objectives':
            analysisData = { foundation: source.foundation, objectives: source.objectives };
            break;
        case 'external-environment':
            analysisData = {
                pestel: source.pestel,
                fiveForces: source.fiveForces,
                competitors: source.competitors,
                drivingForces: source.drivingForces,
            };
            break;
        case 'swot-capabilities':
            analysisData = { swot: source.swot, successFactors: source.successFactors };
            break;
        case 'execution-progress':
            analysisData = { objectives: source.objectives, execution: source.execution };
            break;
        case 'pestel':
            analysisData = { pestel: source.pestel };
            break;
        case 'five-forces':
            analysisData = { fiveForces: source.fiveForces };
            break;
        case 'success-factors':
            analysisData = { successFactors: source.successFactors };
            break;
        case 'competitors':
            analysisData = { competitors: source.competitors };
            break;
        case 'driving-forces':
            analysisData = { drivingForces: source.drivingForces };
            break;
        default:
            analysisData = {
                foundation: source.foundation,
                objectives: source.objectives,
                pestel: source.pestel,
                fiveForces: source.fiveForces,
                swot: source.swot,
                successFactors: source.successFactors,
                competitors: source.competitors,
                drivingForces: source.drivingForces,
                execution: source.execution,
            };
            break;
    }
    return {
        sectionType: sectionIdMapped,
        sectionName: document.sections.find((item) => item.id === sectionId)?.title || label(sectionIdMapped),
        companyName: source.company.name,
        companyIndustry: source.company.industry || '',
        companySize: source.company.size || '',
        sourceCapturedAt: source.capturedAt,
        analysisData: { ...analysisData, loadErrors: source.loadErrors },
    };
}
